import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { colorsMapping, VariantColor } from '@repo/toolkit/colors'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { Column } from '@repo/ui/data-table'
import {
  CheckMarkIcon,
  CreditCardIcon,
  EmailIcon,
  EyeOpenIcon,
  PenIcon,
  PrintIcon
} from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { getStaffExpensesSalaries } from '../../api/travel-expenses/get-staff-expenses-salaries'
import { printStaffsExpensesSalaries } from '../../api/travel-expenses/print-staffs-expenses-salaries'
import { travelExpensesSendEmailToStaffs } from '../../api/travel-expenses/send-email-to-staffs'
import { validateStaffsExpensesSalaries } from '../../api/travel-expenses/validate-staffs-expenses-salaries'
import CountryFlag from '../../components/elements/country-flag'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import SalaryPaymentStatus from '../../components/elements/salary-payment-status'
import SendEmailForm from '../../components/pages/candidates/send-email-form'
import TravelExpensesSalaryStaffFilter from '../../components/pages/travel-expenses/travel-expenses-salary-staff-filter'
import TravelExpensesSalaryStaffTableBlock from '../../components/pages/travel-expenses/travel-expenses-salary-staff-table-block'
import { Paths } from '../../lib/constants/path'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import useTranslate from '../../lib/hooks/use-translate'
import { useUrl } from '../../lib/hooks/use-url'
import { FieldStatusEnum } from '../../lib/types/enums/affected-staff/field-status.enum'
import { StaffPaymentStatusEnum } from '../../lib/types/enums/affected-staff/staff-payment-status.enum'
import { staffPositionEnumMapping } from '../../lib/types/enums/staff/staff-position.enum'
import { staffQualificationEnumMapping } from '../../lib/types/enums/staff/staff-qualifications.enum'
import { StaffProperties } from '../../lib/types/models/staff/properties.staff'
import { StaffInformation } from '../../lib/types/models/staff/staff'
import { StaffExpensesSalaryIdsInput } from '../../lib/types/models/travel-expenses/travel-expenses'
import {
  TravelExpensesSalaryStaff,
  TravelExpensesSalaryStaffsFilters
} from '../../lib/types/models/travel-expenses/travel-expenses-staff'
import { modalButtonConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformTravelExpensesStaffSalaryFiltersToUrl,
  transformUrlToTravelExpensesStaffSalaryFilters
} from '../../lib/utils/filters-mapping/travel-expenses-staff-salary'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../lib/utils/functions/translation.functions'

// Helper Functions

const getRelatedStaff = (data: TravelExpensesSalaryStaff): StaffInformation | undefined => {
  if ('affectedStaff' in data && data.affectedStaff) return data.affectedStaff.staff
  if ('missionToStaff' in data && data.missionToStaff) return data.missionToStaff.staff
  return undefined
}

const getIbanVariant = (isExported: boolean, ibanStatus?: FieldStatusEnum): VariantColor => {
  if (ibanStatus === FieldStatusEnum.EMPTY) return 'danger'
  if (isExported) return 'success'
  return 'warning'
}

const getQualifications = (data: TravelExpensesSalaryStaff) => {
  const affectedQualification =
    'affectedStaff' in data && data.affectedStaff
      ? [data.affectedStaff.staffRequest.qualification]
      : []

  const missionQualifications =
    'missionToStaff' in data && data.missionToStaff
      ? data.missionToStaff.mission.qualifications
      : []

  return [...affectedQualification, ...missionQualifications].filter(Boolean)
}

const getCountryCode = (data: TravelExpensesSalaryStaff) => {
  if ('affectedStaff' in data && data.affectedStaff)
    return data.affectedStaff.staffRequest.country?.code
  return undefined
}

const getCenter = (data: TravelExpensesSalaryStaff) => {
  if ('affectedStaff' in data && data.affectedStaff)
    return data.affectedStaff.staffRequest.centers.map((c) => c.name).join(', ')

  return undefined
}

const getDateRange = (data: TravelExpensesSalaryStaff) => {
  if ('affectedStaff' in data && data.affectedStaff) {
    const { startDate, endDate } = data.affectedStaff.staffRequest
    if (startDate && endDate) return DateFormatter.getDateRangeDDMMYYYY(startDate, endDate)
  }
  if ('missionToStaff' in data && data.missionToStaff?.missionTracking) {
    const { startDate, endDate } = data.missionToStaff.missionTracking
    return DateFormatter.getDateRangeDDMMYYYY(startDate, endDate)
  }
  return undefined
}

const getDuration = (data: TravelExpensesSalaryStaff) => {
  if ('affectedStaff' in data && data.affectedStaff) {
    const { startDate, endDate } = data.affectedStaff.staffRequest
    if (startDate && endDate) {
      return DateFormatter.getDurationInDays(startDate, endDate)
    }
  }
  return undefined
}

const getFormulas = (data: TravelExpensesSalaryStaff) => {
  if ('affectedStaff' in data && data.affectedStaff) {
    return data.affectedStaff.staffRequest.formulas.map((f) => getTranslatedName(f)).join(', ')
  }
  return undefined
}

const getTravelExpensesAmount = (data: TravelExpensesSalaryStaff) => {
  const expenses =
    'affectedStaff' in data && data.affectedStaff
      ? data.affectedStaff.travelExpenses
      : 'missionToStaff' in data && data.missionToStaff
        ? data.missionToStaff.travelExpenses
        : []

  const currency =
    'affectedStaff' in data && data.affectedStaff
      ? data.affectedStaff.travelExpensesCurrency
      : 'missionToStaff' in data && data.missionToStaff
        ? data.missionToStaff.travelExpensesCurrency
        : undefined

  const amount = expenses.reduce((sum, expense) => sum + (expense.validatedAmount ?? 0), 0)

  return { amount, currency }
}

const getDateReceived = (data: TravelExpensesSalaryStaff) => {
  if ('affectedStaff' in data && data.affectedStaff) return data.affectedStaff.travelExpensesSentAt
  if ('missionToStaff' in data && data.missionToStaff)
    return data.missionToStaff.travelExpensesSentAt
  return undefined
}

const getDateValidated = (data: TravelExpensesSalaryStaff) => {
  if ('affectedStaff' in data && data.affectedStaff)
    return data.affectedStaff.travelExpensesValidatedAt
  if ('missionToStaff' in data && data.missionToStaff)
    return data.missionToStaff.travelExpensesValidatedAt
  return undefined
}

const getValidatedBy = (data: TravelExpensesSalaryStaff) => {
  if ('affectedStaff' in data && data.affectedStaff) {
    return data.affectedStaff.travelExpensesValidatedBy
  }
  if ('missionToStaff' in data && data.missionToStaff) {
    return data.missionToStaff.travelExpensesValidatedBy
  }
  return undefined
}

const getPaymentStatus = (data: TravelExpensesSalaryStaff) => {
  if ('affectedStaff' in data && data.affectedStaff) {
    return data.affectedStaff.travelExpensesPaymentStatus
  }
  if ('missionToStaff' in data && data.missionToStaff) {
    return data.missionToStaff.travelExpensesPaymentStatus
  }
  return undefined
}

const getTransferValidatedAt = (data: TravelExpensesSalaryStaff) => {
  if ('affectedStaff' in data && data.affectedStaff)
    return data.affectedStaff.travelExpensesPaymentValidatedAt
  if ('missionToStaff' in data && data.missionToStaff)
    return data.missionToStaff.travelExpensesPaymentValidatedAt
  return undefined
}

const getTransferExportedAt = (data: TravelExpensesSalaryStaff) => {
  if ('affectedStaff' in data && data.affectedStaff)
    return data.affectedStaff.travelExpensesPaymentExportedAt
  if ('missionToStaff' in data && data.missionToStaff)
    return data.missionToStaff.travelExpensesPaymentExportedAt
  return undefined
}

const isRowBlocked = (data: TravelExpensesSalaryStaff): boolean => {
  if ('affectedStaff' in data && data.affectedStaff)
    return data.affectedStaff.travelExpensesBlocked
  if ('missionToStaff' in data && data.missionToStaff)
    return data.missionToStaff.travelExpensesBlocked
  return true
}

const getAvailableMissionToStaffIds = (data: TravelExpensesSalaryStaff[]): number[] => {
  const availableIds: number[] = []
  data.forEach((item) => {
    if (
      'missionToStaff' in item &&
      item.missionToStaff &&
      !item.missionToStaff.travelExpensesBlocked
    )
      availableIds.push(item.missionToStaff.id)
  })
  return availableIds
}

const getAvailableAffectedStaffIds = (data: TravelExpensesSalaryStaff[]): number[] => {
  const availableIds: number[] = []
  data.forEach((item) => {
    if ('affectedStaff' in item && item.affectedStaff && !item.affectedStaff.travelExpensesBlocked)
      availableIds.push(item.affectedStaff.id)
  })
  return availableIds
}

// Main Component
const TravelExpensesSalaryStaffsListPage = () => {
  const tableRef = useRef<DynamicTableRef<TravelExpensesSalaryStaff>>(null)
  const { navigate } = useNavigator()
  const { t } = useTranslate()
  const { downloadDocuments } = useDownloadDocuments()

  const [isTableLoading, setIsTableLoading] = useState(false)
  const { readPagination } = useUrl()
  const [search, setSearch] = useState(() => readPagination().q)
  const { modalState, toggleModal, closeModel, selectedData } = useModalManager<
    'filters' | 'send-email',
    StaffInformation[]
  >(['filters', 'send-email'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<TravelExpensesSalaryStaffsFilters>({
      // Validated transfers are hidden by default, they stay reachable through the filters
      initialFilters: {
        paymentStatuses: [StaffPaymentStatusEnum.PENDING, StaffPaymentStatusEnum.EXPORTED]
      },
      options: { allowSearchParam: true },
      transformFilterToUrl: transformTravelExpensesStaffSalaryFiltersToUrl,
      transformUrlToFilter: transformUrlToTravelExpensesStaffSalaryFilters
    })

  const { data: totalSalaries } = useQuery({
    queryKey: ['staff-expenses-salaries', JSON.stringify(selectedFilter), search],
    queryFn: () => getStaffExpensesSalaries(selectedFilter, search)
  })

  const sendEmailToStaffsMutation = useMutation({
    mutationFn: travelExpensesSendEmailToStaffs,
    onSuccess: () => {
      toast.success('Les emails ont été envoyés avec succès')
      closeModel('send-email')
    },
    onError: showAxiosError
  })

  const printStaffsExpensesSalariesMutation = useMutation({
    mutationFn: printStaffsExpensesSalaries,
    onSuccess: () => toast.success('Les fichiers ont été imprimés avec succès'),
    onError: showAxiosError
  })

  const validateStaffsExpensesSalariesMutation = useMutation({
    mutationFn: validateStaffsExpensesSalaries,
    onSuccess: () => {
      toast.success('Les frais ont été validés avec succès')
      tableRef.current?.refetch()
    },
    onError: showAxiosError
  })

  // Column Definitions
  const columns: Column<TravelExpensesSalaryStaff>[] = [
    {
      accessorKey: 'id',
      header: '#'
    },
    {
      accessorKey: 'lastName',
      header: 'Nom',
      cell: ({ row }) => {
        const staff = getRelatedStaff(row.original)
        return valueRender(staff?.lastName.toUpperCase())
      }
    },
    {
      accessorKey: 'firstName',
      header: 'Prénom',
      cell: ({ row }) => {
        const staff = getRelatedStaff(row.original)
        return valueRender(staff?.firstName)
      }
    },
    {
      accessorKey: 'position',
      header: 'Poste',
      cell: ({ row }) => {
        const staff = getRelatedStaff(row.original)
        if (!staff?.position) return '-'
        return t(staffPositionEnumMapping[staff.position])
      }
    },
    {
      accessorKey: 'qualification',
      header: 'Qualification',
      cell: ({ row }) => {
        const qualifications = getQualifications(row.original)
        if (!qualifications.length) return '-'
        return qualifications.map((q) => t(staffQualificationEnumMapping[q].label)).join(', ')
      }
    },
    {
      accessorKey: 'country',
      header: 'Pays',
      cell: ({ row }) => {
        const country = getCountryCode(row.original)
        if (!country) return '-'
        return <CountryFlag code={country} />
      }
    },
    {
      accessorKey: 'center',
      header: 'Centres',
      cell: ({ row }) => valueRender(getCenter(row.original))
    },
    {
      accessorKey: 'date',
      header: 'Date',
      cell: ({ row }) => <Badge text={valueRender(getDateRange(row.original))} />
    },
    {
      accessorKey: 'duration',
      header: 'Durée',
      cell: ({ row }) => {
        const duration = getDuration(row.original)
        return <Badge text={`${valueRender(duration)} jours`} />
      }
    },
    {
      accessorKey: 'formula',
      header: 'Formules',
      cell: ({ row }) => getFormulas(row.original) || '-'
    },
    {
      accessorKey: 'seniority',
      header: 'Ancienneté',
      cell: ({ row }) =>
        valueRender(row.original, (s) =>
          StaffProperties.getSeniority(getRelatedStaff(s)).toString()
        )
    },
    {
      accessorKey: 'amount',
      header: 'Montant frais',
      cell: ({ row }) => {
        const { amount, currency } = getTravelExpensesAmount(row.original)
        return (
          <div className="w-28">
            {amount !== undefined ? (
              <Badge
                variant="primary"
                text={NumberFormatter.formatCurrency(amount, currency, 'narrowSymbol')}
              />
            ) : (
              '-'
            )}
          </div>
        )
      }
    },
    {
      accessorKey: 'blocked',
      header: 'Ok',
      cell: ({ row }) => (
        <TravelExpensesSalaryStaffTableBlock
          travelExpenseSalaryStaff={row.original}
          onBlockSuccess={() => {
            tableRef.current?.refetch()
            toast.success('Frais bloqués avec succès')
          }}
          onUnblockSuccess={() => {
            tableRef.current?.refetch()
            toast.success('Frais débloqués avec succès')
          }}
        />
      )
    },
    {
      accessorKey: 'ibanStatus',
      header: 'Banque',
      cell: ({ row }) => {
        const staff = getRelatedStaff(row.original)
        const ibanStatus = staff?.ibanStatus
        const isExported = !!staff?.ibanExportedAt
        const variant = getIbanVariant(isExported, ibanStatus)

        return (
          <Badge
            variant={variant}
            icon={<CreditCardIcon color={colorsMapping[variant].text} />}
            size="flexible"
          />
        )
      }
    },
    {
      accessorKey: 'dateReceived',
      header: 'Date réception',
      cell: ({ row }) => {
        const date = getDateReceived(row.original)
        return date ? (
          <Badge text={valueRender(date, (d) => DateFormatter.DDMMYYYY(d))} variant="grey" />
        ) : (
          '-'
        )
      }
    },
    {
      accessorKey: 'travelExpensesValidatedAt',
      header: 'Date de validation',
      cell: ({ row }) => {
        const date = getDateValidated(row.original)
        return date ? (
          <Badge text={valueRender(date, (d) => DateFormatter.DDMMYYYY(d))} variant="grey" />
        ) : (
          '-'
        )
      }
    },
    {
      accessorKey: 'validatedBy',
      header: 'Validée par',
      cell: ({ row }) => {
        const validator = getValidatedBy(row.original)
        return valueRender(validator, (user) => user.firstName)
      }
    },
    {
      accessorKey: 'viewExpenseReport',
      header: 'Voir la note de frais',
      cell: ({ row }) => {
        const handleClick = () => {
          if (row.original.affectedStaffId) {
            navigate(
              Paths.AFFECTED_STAFF_EXPENSE_REPORT_DETAILS_PAGE.replace(
                ':id',
                row.original.affectedStaffId.toString()
              )
            )
          } else if (row.original.missionToStaffId) {
            navigate(
              Paths.MISSION_TO_STAFF_EXPENSE_REPORT_DETAILS_PAGE.replace(
                ':id',
                row.original.missionToStaffId.toString()
              )
            )
          }
        }

        return (
          <Button
            variant="ghost"
            label="Voir la note de frais"
            icon={EyeOpenIcon}
            onClick={handleClick}
          />
        )
      }
    },
    {
      accessorKey: 'paymentStatus',
      header: 'Statut du paiement',
      cell: ({ row }) => {
        const paymentStatus = getPaymentStatus(row.original)
        const validatedAt = getTransferValidatedAt(row.original)
        const exportedAt = getTransferExportedAt(row.original)
        if (!paymentStatus || paymentStatus === StaffPaymentStatusEnum.PENDING) return '-'
        return (
          <SalaryPaymentStatus
            paymentStatus={paymentStatus}
            paymentValidatedAt={validatedAt}
            paymentExportedAt={exportedAt}
          />
        )
      }
    }
  ]

  const handleRefetch = () => tableRef.current?.refetch()

  return (
    <div>
      <UserNavBar title="Paiement et suivi des frais" icon={CreditCardIcon} />
      <div className="h-4" />

      <DynamicDataTable<TravelExpensesSalaryStaff, TravelExpensesSalaryStaffsFilters>
        ref={tableRef}
        title="Paiement notes de frais"
        columns={columns}
        onLoadingChange={setIsTableLoading}
        selectable
        disableRow={isRowBlocked}
        allowSearchParam
        onSearchChange={setSearch}
        version="FULL"
        permissions={{ delete: false }}
        endpoint={API.travelExpenses.getTravelExpensesSalaryStaffs.url}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onFiltersClick={() => toggleModal('filters')}
        actions={[
          {
            type: 'multiple',
            icon: PenIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Editer les virements',
            fn: async (data) => {
              const affectedStaffIds = getAvailableAffectedStaffIds(data)
              const missionToStaffIds = getAvailableMissionToStaffIds(data)

              if (affectedStaffIds.length === 0 && missionToStaffIds.length === 0) {
                toast.error("Aucun frais disponible pour l'édition des virements")
                return
              }

              await downloadDocuments<StaffExpensesSalaryIdsInput>({
                endpoint: API.travelExpenses.exportStaffsExpensesSalariesToXml.url,
                data: { affectedStaffIds, missionToStaffIds }
              })

              handleRefetch()
            }
          },
          {
            type: 'multiple',
            icon: CheckMarkIcon,
            variant: 'ghost',
            label: 'Virement validé',
            fn: async (data) => {
              const affectedStaffIds = getAvailableAffectedStaffIds(data)
              const missionToStaffIds = getAvailableMissionToStaffIds(data)

              if (affectedStaffIds.length === 0 && missionToStaffIds.length === 0) return

              if (
                data.some((mt) => {
                  const paymentStatus = getPaymentStatus(mt)
                  return paymentStatus === StaffPaymentStatusEnum.PENDING
                })
              ) {
                toast.error(
                  'Tous les éléments sélectionnés doivent être au statut « Fichier de virement exporté ».'
                )
                return
              }
              await validateStaffsExpensesSalariesMutation.mutateAsync({
                affectedStaffIds,
                missionToStaffIds
              })
              handleRefetch()
            }
          },
          {
            type: 'multiple',
            icon: EmailIcon,
            label: 'E-mail groupé',
            variant: 'ghost',
            fn: (data) => {
              const staffs = data
                .map((staff) => getRelatedStaff(staff))
                .filter((staff): staff is StaffInformation => staff !== undefined)
              toggleModal('send-email', staffs)
            }
          },
          {
            type: 'multiple',
            variant: 'ghost',
            icon: PrintIcon,
            label: 'imprimer les notes de frais',
            fn: async (data) => {
              const affectedStaffIds = getAvailableAffectedStaffIds(data)
              const missionToStaffIds = getAvailableMissionToStaffIds(data)

              if (affectedStaffIds.length === 0 && missionToStaffIds.length === 0) {
                toast.error("Aucun frais disponible pour l'édition des virements")
                return
              }
              await printStaffsExpensesSalariesMutation.mutateAsync({
                affectedStaffIds,
                missionToStaffIds
              })
              handleRefetch()
            }
          }
        ]}
        footerclassName="bg-primary-light"
        footerCells={[
          {
            accessorKey: 'id',
            value: (
              <Typography variant="caption" color={tailwindColors.primary.DEFAULT}>
                Total
              </Typography>
            )
          },
          {
            accessorKey: 'amount',
            value: (
              <div className="flex flex-col gap-2 py-2">
                {totalSalaries?.totals.map(({ currency, total }) => (
                  <Badge
                    key={currency}
                    variant="orange"
                    text={NumberFormatter.formatCurrency(total, currency, 'narrowSymbol')}
                  />
                ))}
              </div>
            )
          }
        ]}
      />

      <FilterDrawer<TravelExpensesSalaryStaffsFilters>
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <TravelExpensesSalaryStaffFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      {selectedData && (
        <Modal
          isOpen={modalState['send-email']}
          title={'Envoyer un email'}
          icon={EmailIcon}
          size="medium"
          hideActions
          onCancel={() => closeModel('send-email')}
        >
          <SendEmailForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={{
              emails: [...new Set(selectedData.flatMap((data) => data.user?.email ?? []))],
              subject: '',
              body: ''
            }}
            labels={selectedData.map((data) => {
              const email = data.user?.email
              return `${toFullName(data.user?.firstName, data.user?.lastName, { options: { mode: 'lastname-firstname' } })} - ${email}`
            })}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('send-email'),
              isLoading: sendEmailToStaffsMutation.isPending,
              onConfirm: (values) =>
                sendEmailToStaffsMutation.mutate({
                  ...values,
                  emails: values.emails
                })
            })}
          />
        </Modal>
      )}
    </div>
  )
}

export default TravelExpensesSalaryStaffsListPage
